import type * as pf from '@amirada/lib-shared'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { PARAM, utils as wiloUtils } from '@amirada/lib-wilo-shared'
import type * as tp from '@base/utils/types'
import { useAppIndexStore } from '@base/stores/indexStore'
import { def_localCartPrefix } from '@base/utils/constants'
import { ut_buildParamData, ut_getText, ut_navigateNewRoute } from '@base/utils/utils'
import type { ECartContentType } from '~/utils/constants'
import { ECartUserDefined, def_cartContentMeta, def_cartLeafLevel, def_coverPicsRoot, def_newProjectUrlId } from '~/utils/constants'
import type * as tpWilo from '~/utils/types'
import { ut_wilo_pictureSize } from '~/utils/utils'

/*
 * The helpers of the price list (ct_price_list_create) — moved out of utils.ts on 14.10.2026, where
 * they stood scattered between price formatting, spaix and the description blocks:
 *
 *   markers      what a structure row is and shows (cartContent, contentLabel, contentLevel, …)
 *   hierarchy    the tree read off the flat cart (subtreeRange, enclosingMarker)
 *   tiles        the picture of a project / template tile
 *   project url  which project is open (`tab`)
 *
 * What a print page asks about the price list (ut_wilo_isPriceList, salesFilter, the series price
 * groups) stays in utils.ts: it belongs to the detail pages that are rendered into the pdf, not here.
 */

/*
 * The text a structure marker (chapter, subchapter, group, pdf) shows in one language — on the row,
 * in the sort view and in the pdf's table of contents alike.
 *
 * Order: the language, english, any language someone gave a text (lib-wilo-shared priceListText —
 * the one place that order lives), and only then `label`, the type's default name or the template's
 * english. Pass the language the text is FOR: the app's lcda on screen, the pdf's language in print.
 *
 * A marker with no text of its own in ANY language — a hand-made one nobody renamed — shows its type
 * name through ut_getText (def_cartContentMeta.textKey, 24.09.2026), in the app's language: the
 * stored `label` is the English default and stays only for a type the table does not know. Template
 * markers always bring texts and never get here.
 */
export function ut_wilo_contentLabel(content: tpWilo.ICartContent | undefined, lang: string): string {
  if (!content) { return '' }
  const own: string = wiloUtils.priceListText(content.labels, lang)
  if (own) { return own }
  const textKey: string | undefined = def_cartContentMeta[content.type]?.textKey
  return textKey ? ut_getText(textKey) : content.label || ''
}

// The text of a node of the price-list export (a template tile) in one language, same order as above.
export function ut_wilo_templateText(node: pfWilo.IPriceListNode, lang: string): string {
  return wiloUtils.priceListText(node.descs, lang) || node.desc || ''
}

/*
 * The marker after a rename: the typed text becomes the text of `lang` and nothing else, the other
 * languages stay as they are. An empty entry removes the language, so the row falls back again
 * instead of carrying an empty text.
 */
export function ut_wilo_renameContent(content: tpWilo.ICartContent, lang: string, text: string): tpWilo.ICartContent {
  const key: string = lang.toLowerCase()
  const labels: pfWilo.TPriceListTexts = Object.fromEntries(Object.entries(content.labels || {}).filter(([code]) => code !== key))
  if (text.trim()) { labels[key] = text }
  return { ...content, labels }
}

/*
 * The structure marker a cart row carries (chapter, subchapter, group, pdf), or undefined for a series
 * row. ⭐ THE test for "is this a local row": every `local:` row is a marker and only markers are local —
 * they live in the client alone and must never be sent to the server as a namePath or looked up as a
 * node (see feature_cart_content_items). One place for it, instead of a namePath-prefix test here and
 * a userDefined test there.
 */
export function ut_wilo_cartContent(item: pf.CartItem): tpWilo.ICartContent | undefined {
  return item.userDefined?.[ECartUserDefined.cart_us_content]
}

// The row's place in the price-list hierarchy (def_cartContentMeta.level): 1–3 for the markers that
// own the rows below them, def_cartLeafLevel for a pdf row and for every series.
export function ut_wilo_contentLevel(item: pf.CartItem): number {
  const type: ECartContentType | undefined = ut_wilo_cartContent(item)?.type
  return type ? def_cartContentMeta[type].level : def_cartLeafLevel
}

/*
 * A new marker row: a `local:` namePath that is unique by itself, and the marker in userDefined — the
 * only field stripCart keeps, so the only one that survives being saved. The same shape whether it is
 * taken over from a Wilo template (every language CMI has in `labels`, the english as `label`) or
 * inserted by hand (the type's default name, `labels` empty until someone renames it), so both switch
 * language the same way.
 */
export function ut_wilo_newContentItem(type: ECartContentType, label: string, labels: pfWilo.TPriceListTexts = {}): pf.CartItem {
  const content: tpWilo.ICartContent = { type, label, labels }
  return {
    namePath: def_localCartPrefix + crypto.randomUUID(),
    userDefined: { [ECartUserDefined.cart_us_content]: content },
  }
}

/*
 * The picture of a price-list tile, template or project: the cover it carries (template `pdf.cover`,
 * project CART_LIST `cover`), else the Wilo placeholder — the same for both kinds of tile (Christoph
 * 12.10.2026; the customer may still decide otherwise). A full URL is a database picture and shows in
 * size 2, the tile size of the series list; the standard covers exist only in their one size.
 * An uploaded cover (project CART_LIST `coverCustom`, a path relative to the data host) wins over
 * both and shows as it is — it has no sizes.
 */
export function ut_wilo_coverTilePicture(cover: string | null | undefined, coverCustom?: string | null): string {
  if (coverCustom) { return useRuntimeConfig().public.dataHost + '/' + coverCustom }
  if (!cover) { return useAppConfig().altPic }
  return /^https?:\/\//.test(cover) ? ut_wilo_pictureSize(cover, 2) : def_coverPicsRoot + cover
}

/*
 * The hierarchy questions of the price-list cart, on the flat item list it is built from.
 *
 * The cart is and stays a flat array — insert positions, PDF order and the server round trip all
 * count indices. The tree is only a reading of it: a marker (chapter 1 < subchapter 2 < group 3)
 * owns every following row of a deeper level, up to the next row of its own or a higher level.
 * Collapsing already reads it that way; hiding and hierarchical deleting ask the same question, so
 * it is answered here once instead of three times in the component.
 *
 * Deliberately no CartItem in the signatures: the caller passes the levels it already computed.
 * That keeps both functions free of the userDefined shape.
 */

/**
 * Index range `[start, end)` of the rows belonging under `index` — its subtree, without the row
 * itself. A row that owns nothing (a series, or a marker with nothing below it) yields an empty
 * range, so callers need no special case.
 */
export function ut_wilo_subtreeRange(levels: number[], index: number): [number, number] {
  const level: number | undefined = levels[index]
  if (level === undefined) { return [index + 1, index + 1] }

  let end: number = index + 1
  while (end < levels.length && (levels[end] ?? 0) > level) { end++ }
  return [index + 1, end]
}

/**
 * The marker that encloses `position` for something of `level`, or -1 when nothing does. Used when
 * a row is inserted: it takes over that marker's hidden state, because otherwise it would show up
 * visible in the middle of a hidden section.
 *
 * `level` is passed rather than read from `levels[position]` — at insert time the row is not in the
 * list yet, and its level is exactly what the caller is about to give it.
 */
export function ut_wilo_enclosingMarker(levels: number[], position: number, level: number): number {
  for (let i = position - 1; i >= 0; i--) {
    const own: number | undefined = levels[i]
    if (own !== undefined && own < level) { return i }
  }
  return -1
}

/*
 * The open project in the url — `tab`, which is client-only (`IParamQueryUrlOnly`) and never reaches
 * the server, and which the price list page has free because it shows no tabs of its own.
 *
 * `doPush` is the whole point of the parameter: opening a project pushes, so the browser's back
 * button leads to the project management. Every other write replaces, so that correcting the
 * address (first save) or clearing it (reload fallback) does not add a step to go back through.
 *
 * ⚑ Always through `ut_navigateNewRoute`, never `navigateTo`/`router.push`: country, language, the
 * query parameters to carry over, the hash and the embedded cases all hang off
 * `fn_getNewRouterParams` behind it.
 */
export function ut_wilo_setProjectUrl(id: string, doPush: boolean): void {
  ut_navigateNewRoute('', { tab: id }, '', doPush)
}

// The project id an incoming url asks for: a uuid, the placeholder for a locked template, or ''.
export function ut_wilo_projectIdFromUrl(): string {
  return useAppIndexStore().state.paramData.tab || ''
}

/*
 * Is the url still talking about this project? The url is what decides which project is open, so a
 * project still held in memory while the address names another one — or none at all, after the back
 * button — must not act as if it were on screen. Auto-save asks before every write.
 */
export function ut_wilo_projectUrlMatches(project: tpWilo.IOpenProject | undefined): boolean {
  return ut_wilo_projectIdFromUrl() === (project ? (project.id || def_newProjectUrlId) : '')
}

/*
 * A price table of a series is one query string — psx, sort and the switched-off columns (dcl), empty
 * parts left out. Shared by the table editor (CartSeriesPriceTable) and the tables built when a project
 * is taken over from a template (usePriceListTemplate), so both write the same form.
 */
export function ut_wilo_priceTableQuery(psx: string, sort: string, dcl: string): string {
  const q = new URLSearchParams()
  if (psx) { q.set('psx', psx) }
  if (sort) { q.set('sort', sort) }
  if (dcl) { q.set('dcl', dcl) }
  return q.toString()
}

// A psx from selection items "filterName|valueGUID" — the values of one filter grouped behind it,
// "filter|guid1|guid2,filter2|guid3", the form the mask writes.
export function ut_wilo_psxFromItems(items: string[]): string {
  const byFilter: Record<string, string[]> = {}
  for (const item of items) {
    const filter: string = item.split('|')[0] || ''
    if (!filter) { continue }
    if (!byFilter[filter]) { byFilter[filter] = [] }
    byFilter[filter].push(item.split('|')[1] || '')
  }
  return Object.entries(byFilter).map(([filter, values]) => [filter, ...values].join('|')).join(',')
}

/*
 * The request paramData for the mask/list of a series in the price-list context, outside the url.
 *
 * Price-list context (param16) keeps the columns flagged list_feature_visible, which the catalogue answer
 * has removed. Set on the LOCAL copy only — the page's own url carries no param16 (the print does, which
 * is how the same flag reaches the headless render). It also lands in the store cache key, so the same
 * series looked at in the catalogue and configured here cannot share an entry.
 */
export function ut_wilo_priceListMaskParam(objPath: string, psx: string, sort: string): tp.IParamData {
  const param: tp.IParamData = ut_buildParamData(useAppIndexStore().state.paramData, { objPath, psx, sort })
  param[PARAM.priceList] = 1
  return param
}
